import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import { ElNotification } from 'element-plus'

class WebSocketService {
  constructor() {
    this.client = null
    this.connected = false
    this.subscriptions = new Map()
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 3000
    this.heartbeatTimer = null
    
    // 防止重复弹窗的已显示通知记录
    this.shownNotifications = new Map() // 改为Map以存储键值对
    this.notificationTimeout = 60000 // 1分钟内不重复显示相同通知
  }

  connect(userId) {
    console.log('WebSocketService.connect 被调用，用户ID:', userId)
    
    // 保存当前用户ID
    this.currentUserId = userId
    
    if (this.client && this.connected) {
      console.log('WebSocket already connected')
      return
    }

    // 如果存在旧的连接，先断开
    if (this.client) {
      console.log('断开旧连接')
      this.disconnect()
    }

    // 创建SockJS连接 - 使用配置的WebSocket URL
    let baseUrl
    if (process.env.NODE_ENV === 'production') {
      // 生产环境使用相对路径
      baseUrl = '/sehome'
    } else {
      // 开发环境使用完整的后端地址 - 根据application.yml，后端端口是9000
      baseUrl = 'http://localhost:9000/sehome'
    }
    
    const wsUrl = `${baseUrl}/ws`
    console.log('WebSocket连接URL:', wsUrl)
    console.log('当前环境:', process.env.NODE_ENV)
    console.log('环境变量检查:', {
      VUE_APP_API_BASE_URL: process.env.VUE_APP_API_BASE_URL,
      NODE_ENV: process.env.NODE_ENV
    })
    
    try {
      console.log('尝试创建SockJS连接:', wsUrl)
      const socket = new SockJS(wsUrl)
      console.log('SockJS连接已创建，当前状态:', socket.readyState)
      
      // 添加SockJS事件监听
      socket.onopen = () => {
        console.log('SockJS连接已打开')
      }
      socket.onclose = (event) => {
        console.log('SockJS连接已关闭:', event.code, event.reason)
      }
      socket.onerror = (error) => {
        console.error('SockJS连接错误:', error)
      }
      
      this.client = new Client({
        webSocketFactory: () => socket,
        reconnectDelay: this.reconnectDelay,
        heartbeatIncoming: 25000,
        heartbeatOutgoing: 25000,
        connectHeaders: {
          'X-User-Id': userId
        },
        
        onConnect: (frame) => {
          console.log('WebSocket连接成功:', frame)
          console.log('WebSocket连接详情:', {
            connected: this.connected,
            client: this.client,
            subscriptions: this.subscriptions.size,
            frame: frame
          })
          this.connected = true
          this.reconnectAttempts = 0
          
          // 发送连接消息
          this.send('/app/connect', { userId })
          
          // 启动心跳
          this.startHeartbeat()
          
          // 订阅用户专属通道
          this.subscribeToUserChannels(userId)
          
          // 触发连接成功事件
          this.onConnected && this.onConnected()
        },
        
        onDisconnect: (frame) => {
          console.log('WebSocket断开连接:', frame)
          this.connected = false
          this.stopHeartbeat()
          
          // 清理订阅记录
          this.subscriptions.clear()
          
          // 触发断开连接事件
          this.onDisconnected && this.onDisconnected()
        },
        
        onStompError: (frame) => {
          console.error('WebSocket STOMP错误:', frame.headers['message'])
          console.error('STOMP错误详情:', {
            command: frame.command,
            headers: frame.headers,
            body: frame.body,
            frame: frame
          })
          
          // 更新连接状态
          this.connected = false
          
          // 触发错误事件
          this.onError && this.onError(frame)
          
          // 尝试重连
          this.attemptReconnect(userId)
        },
        
        onWebSocketError: (event) => {
          console.error('WebSocket错误:', event)
          console.error('WebSocket错误详情:', {
            type: event.type,
            target: event.target,
            readyState: event.target?.readyState,
            url: event.target?.url
          })
          
          // 更新连接状态
          this.connected = false
          
          // 触发错误事件
          this.onError && this.onError(event)
          
          // 尝试重连
          this.attemptReconnect(userId)
        }
      })

      console.log('STOMP客户端已创建，开始激活连接...')
      console.log('客户端配置:', {
        reconnectDelay: this.reconnectDelay,
        heartbeatIncoming: 20000,
        heartbeatOutgoing: 20000
      })
      
      // 激活客户端连接
      this.client.activate()
      
      // 设置连接超时检查
      setTimeout(() => {
        if (!this.connected) {
          console.error('WebSocket连接超时')
          this.connected = false
          this.onError && this.onError(new Error('连接超时'))
        }
      }, 10000) // 10秒超时
      
    } catch (error) {
      console.error('创建WebSocket连接失败:', error)
      this.connected = false
      this.onError && this.onError(error)
    }
  }

  disconnect() {
    this.stopHeartbeat()
    
    if (this.client) {
      // 取消所有订阅
      this.subscriptions.forEach((subscription) => {
        subscription.unsubscribe()
      })
      this.subscriptions.clear()
      
      // 断开连接
      this.client.deactivate()
      this.client = null
    }
    
    this.connected = false
    console.log('WebSocket连接已断开')
  }

  subscribeToUserChannels(userId) {
    console.log('开始订阅用户专属通道，用户ID:', userId)
    
    // 订阅告警通知
    this.subscribe('/user/queue/alarms', (message) => {
      console.log('=== WebSocket收到原始消息 ===')
      console.log('原始消息对象:', message)
      console.log('消息体内容:', message.body)
      console.log('消息头:', message.headers)
      
      try {
        const alarmNotification = JSON.parse(message.body)
        console.log('=== 解析后的告警通知 ===')
        console.log('告警通知内容:', alarmNotification)
        console.log('当前WebSocket状态:', {
          connected: this.connected,
          client: !!this.client,
          subscriptions: this.subscriptions.size,
          userId: userId
        })
        
        // 检查是否已经显示过这个告警通知（防止重复弹窗）
        if (this.isNotificationAlreadyShown(alarmNotification)) {
          console.log('告警通知已显示过，跳过重复弹窗:', alarmNotification.alarmId)
          return
        }
        
        // 标记通知已显示（防止重复处理）
        this.markNotificationAsShown(alarmNotification)
        
        // 显示告警弹窗
        console.log('开始显示告警弹窗...')
        this.showAlarmNotification(alarmNotification)
        console.log('告警弹窗显示完成')
        
        // 触发告警通知事件（用于其他处理，如音效、通知等，但不显示弹窗）
        if (this.onAlarmNotification) {
          console.log('触发onAlarmNotification事件')
          this.onAlarmNotification(alarmNotification)
        } else {
          console.warn('onAlarmNotification事件处理器未设置')
        }
        
        // 发送浏览器通知确认收到了消息
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('WebSocket告警收到', {
            body: `收到告警: ${alarmNotification.title || alarmNotification.alarmType}`,
            icon: '/favicon.ico'
          })
        }
        
      } catch (error) {
        console.error('解析WebSocket告警消息失败:', error)
        console.error('原始消息:', message.body)
      }
    })

    // 订阅一般通知
    this.subscribe('/user/queue/notifications', (message) => {
      const notification = JSON.parse(message.body)
      console.log('收到通知:', notification)
      
      // 触发通知事件
      this.onNotification && this.onNotification(notification)
    })

    // 订阅连接确认
    this.subscribe('/user/queue/connection', (message) => {
      const connectionInfo = JSON.parse(message.body)
      console.log('连接确认:', connectionInfo)
    })

    // 订阅心跳响应
    this.subscribe('/user/queue/heartbeat', (message) => {
      const heartbeatInfo = JSON.parse(message.body)
      console.log('心跳响应:', heartbeatInfo)
    })

    // 订阅系统广播
    this.subscribe('/user/queue/system', (message) => {
      const systemNotification = JSON.parse(message.body)
      console.log('收到系统广播:', systemNotification)
      
      // 触发系统通知事件
      this.onSystemNotification && this.onSystemNotification(systemNotification)
    })

    // 订阅全局告警广播（用于测试）
    this.subscribe('/topic/alarms', (message) => {
      console.log('=== 收到全局告警广播 ===')
      console.log('广播消息:', message)
      console.log('消息体:', message.body)
      
      try {
        const alarmNotification = JSON.parse(message.body)
        console.log('解析后的广播告警:', alarmNotification)
        
        // 显示告警弹窗
        this.showAlarmNotification(alarmNotification)
        
        // 触发告警通知事件
        if (this.onAlarmNotification) {
          this.onAlarmNotification(alarmNotification)
        }
        
        // 发送浏览器通知确认收到了消息
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('广播告警收到', {
            body: `收到广播告警: ${alarmNotification.title || alarmNotification.alarmType}`,
            icon: '/favicon.ico'
          })
        }
        
      } catch (error) {
        console.error('解析广播告警消息失败:', error)
      }
    })
    
    console.log('用户专属通道订阅完成，总订阅数:', this.subscriptions.size)
  }

  subscribe(destination, callback) {
    if (!this.client || !this.connected) {
      console.warn('WebSocket未连接，无法订阅:', destination)
      return null
    }

    // 如果已经订阅过这个通道，先取消订阅
    if (this.subscriptions.has(destination)) {
      const oldSubscription = this.subscriptions.get(destination)
      if (oldSubscription) {
        oldSubscription.unsubscribe()
        console.log('取消旧订阅:', destination)
      }
    }

    const subscription = this.client.subscribe(destination, callback)
    this.subscriptions.set(destination, subscription)
    console.log('订阅成功:', destination)
    
    return subscription
  }

  send(destination, body = {}) {
    console.log('尝试发送WebSocket消息:', { destination, body, connected: this.connected, client: !!this.client })
    
    if (!this.client) {
      console.error('WebSocket客户端未创建，无法发送消息')
      return false
    }
    
    if (!this.connected) {
      console.error('WebSocket未连接，无法发送消息。当前状态:', {
        connected: this.connected,
        client: !!this.client,
        subscriptions: this.subscriptions.size
      })
      return false
    }

    try {
      this.client.publish({
        destination,
        body: JSON.stringify(body)
      })
      console.log('WebSocket消息发送成功:', destination)
      return true
    } catch (error) {
      console.error('发送WebSocket消息失败:', error)
      return false
    }
  }

  startHeartbeat() {
    this.stopHeartbeat()
    
    this.heartbeatTimer = setInterval(() => {
      if (this.connected) {
        this.send('/app/heartbeat', { timestamp: Date.now() })
      }
    }, 30000) // 每30秒发送一次心跳
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  attemptReconnect(userId) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('达到最大重连次数，停止重连')
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1) // 指数退避
    
    console.log(`${delay / 1000}秒后尝试第${this.reconnectAttempts}次重连...`)
    
    setTimeout(() => {
      if (!this.connected) {
        console.log(`开始第${this.reconnectAttempts}次重连...`)
        this.connect(userId)
      }
    }, delay)
  }

  showNotification(notification) {
    console.log('=== 开始显示通知 ===')
    console.log('通知数据:', notification)
    
    try {
      // 使用ElementPlus显示通知弹窗
      const elNotification = ElNotification({
        title: '📢 新通知',
        message: `
          <div style="margin-bottom: 8px;">
            <strong>${notification.title}</strong>
          </div>
          <div style="margin-bottom: 4px; color: #666;">
            ${notification.content}
          </div>
          <div style="font-size: 12px; color: #999;">
            时间: ${new Date().toLocaleTimeString()}
          </div>
        `,
        type: 'info',
        duration: 5000, // 5秒后自动关闭
        position: 'top-right',
        customClass: 'test-notification',
        dangerouslyUseHTMLString: true,
        showClose: true
      })
      
      console.log('ElementPlus通知弹窗已创建:', elNotification)
      console.log('=== 通知弹窗显示完成 ===')
      
    } catch (error) {
      console.error('显示通知弹窗失败:', error)
    }
  }

  showAlarmNotification(alarmNotification) {
    console.log('=== 开始显示告警弹窗 ===')
    console.log('告警数据:', alarmNotification)
    
    try {
      // 使用ElementPlus显示告警通知弹窗
      const notification = ElNotification({
        title: '⚠️ 新告警通知',
        message: this.formatAlarmMessage(alarmNotification),
        type: this.getNotificationType(alarmNotification.alarmLevel),
        duration: 0, // 不自动关闭，需要用户手动关闭
        position: 'top-right',
        customClass: `alarm-notification alarm-level-${alarmNotification.alarmLevel}`,
        dangerouslyUseHTMLString: true,
        showClose: true,
        onClick: () => {
          // 点击通知时跳转到告警中心并高亮该告警
          if (window.$router) {
            window.$router.push(`/alarm-center?highlight=${alarmNotification.alarmId}`)
          }
          notification.close()
        }
      })
      
      console.log('ElementPlus弹窗已创建:', notification)

      // 播放告警提示音
      this.playAlarmSound(alarmNotification.alarmLevel)
      
      // 如果是紧急或高级别告警，添加额外的浏览器通知
      if (['3', '4'].includes(alarmNotification.alarmLevel)) {
        this.showBrowserNotification(alarmNotification)
      }
      
      console.log('=== 告警弹窗显示完成 ===')
      
    } catch (error) {
      console.error('显示告警弹窗失败:', error)
    }
  }
  
  formatAlarmMessage(alarmNotification) {
    return `
      <div style="margin-bottom: 8px;">
        <strong>${alarmNotification.deviceName}</strong>
      </div>
      <div style="margin-bottom: 4px; color: #666;">
        ${alarmNotification.title}
      </div>
      <div style="font-size: 12px; color: #999;">
        位置: ${alarmNotification.location || '未知'}
      </div>
      <div style="font-size: 12px; color: #999;">
        时间: ${new Date().toLocaleTimeString()}
      </div>
    `
  }
  
  showBrowserNotification(alarmNotification) {
    // 检查浏览器通知权限
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification('🚨 紧急告警', {
          body: `${alarmNotification.deviceName}: ${alarmNotification.title}`,
          icon: '/favicon.ico',
          requireInteraction: true, // 需要用户交互才能关闭
          tag: `alarm-${alarmNotification.alarmId}` // 防止重复通知
        })
      } else if (Notification.permission !== 'denied') {
        // 请求通知权限
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification('🚨 紧急告警', {
              body: `${alarmNotification.deviceName}: ${alarmNotification.title}`,
              icon: '/favicon.ico',
              requireInteraction: true,
              tag: `alarm-${alarmNotification.alarmId}`
            })
          }
        })
      }
    }
  }

  getNotificationType(alarmLevel) {
    switch (alarmLevel) {
      case '4': // 紧急
        return 'error'
      case '3': // 高
        return 'warning'
      case '2': // 中
        return 'info'
      case '1': // 低
        return 'success'
      default:
        return 'info'
    }
  }

  playAlarmSound(alarmLevel) {
    try {
      // 根据告警级别播放不同的提示音
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      
      // 创建振荡器
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      // 连接节点
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // 根据告警级别设置不同的频率和音量
      let frequency = 800 // 默认频率
      let volume = 0.3    // 默认音量
      
      switch (alarmLevel) {
        case '4': // 紧急 - 高频急促
          frequency = 1200
          volume = 0.5
          break
        case '3': // 高 - 中频
          frequency = 1000
          volume = 0.4
          break
        case '2': // 中 - 普通频率
          frequency = 800
          volume = 0.3
          break
        case '1': // 低 - 低频
          frequency = 600
          volume = 0.2
          break
      }
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.5)
      
    } catch (error) {
      console.warn('播放告警提示音失败:', error)
    }
  }

  // 防止重复弹窗
  isNotificationAlreadyShown(notification) {
    const notificationKey = `${notification.alarmId}-${notification.deviceName}-${notification.title}`
    const now = Date.now()

    // 检查是否在短时间内显示过
    if (this.shownNotifications.has(notificationKey)) {
      const lastShownTime = this.shownNotifications.get(notificationKey)
      if (now - lastShownTime < this.notificationTimeout) {
        return true
      }
    }

    return false
  }

  // 标记通知已显示
  markNotificationAsShown(notification) {
    const notificationKey = `${notification.alarmId}-${notification.deviceName}-${notification.title}`
    const now = Date.now()
    
    // 记录当前通知的显示时间
    this.shownNotifications.set(notificationKey, now)
    
    // 设置定时器，在超时后清理记录
    setTimeout(() => {
      this.shownNotifications.delete(notificationKey)
    }, this.notificationTimeout)
  }

  // 测试订阅
  testSubscription() {
    console.log('=== 测试WebSocket订阅 ===')
    
    if (!this.client || !this.connected) {
      console.warn('WebSocket未连接，无法测试订阅')
      return false
    }
    
    // 测试订阅一个公共通道
    const testSubscription = this.client.subscribe('/topic/test', (message) => {
      console.log('收到测试消息:', message)
    })
    
    console.log('测试订阅已创建:', testSubscription)
    
    // 发送测试消息
    this.send('/app/test', { message: '测试消息', timestamp: Date.now() })
    
    return true
  }

  // 检查连接和订阅状态
  getConnectionStatus() {
    return {
      connected: this.connected,
      client: !!this.client,
      subscriptions: this.subscriptions.size,
      subscriptionDetails: Array.from(this.subscriptions.keys()),
      userId: this.currentUserId,
      // 添加更详细的订阅信息
      subscriptionInfo: Array.from(this.subscriptions.entries()).map(([destination, subscription]) => ({
        destination,
        subscriptionId: subscription.id,
        active: subscription.active
      })),
      // 添加连接详情
      connectionDetails: {
        reconnectAttempts: this.reconnectAttempts,
        maxReconnectAttempts: this.maxReconnectAttempts,
        reconnectDelay: this.reconnectDelay,
        heartbeatTimer: !!this.heartbeatTimer
      }
    }
  }
  
  // 强制重连方法（用于调试）
  forceReconnect(userId) {
    console.log('强制重连WebSocket，用户ID:', userId)
    this.disconnect()
    setTimeout(() => {
      this.connect(userId)
    }, 1000)
  }

  // 事件处理器（可由外部设置）
  onConnected = null
  onDisconnected = null
  onError = null
  onAlarmNotification = null
  onNotification = null
  onSystemNotification = null
}

// 创建全局WebSocket实例
const websocketService = new WebSocketService()

export default websocketService